import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { triggerRevalidation } from '@/lib/utils'
import type { Tables, TablesInsert, TablesUpdate } from '@/types/supabase'

type MusicRelease = Tables<'music_releases'>
type MusicTrack = Tables<'music_tracks'>

// ============================================================
// RELEASES
// ============================================================

export function useMusicReleases() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['music_releases', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('music_releases')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('display_order', { ascending: true })
        .order('release_date', { ascending: false })

      if (error) throw error
      return data as MusicRelease[]
    },
  })
}

export function useMusicRelease(releaseId: string | undefined) {
  return useQuery({
    queryKey: ['music_releases', releaseId, 'detail'],
    enabled: !!releaseId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('music_releases')
        .select('*')
        .eq('id', releaseId!)
        .single()

      if (error) throw error
      return data as MusicRelease
    },
  })
}

export function useMusicTracks(releaseId: string | undefined) {
  return useQuery({
    queryKey: ['music_tracks', releaseId],
    enabled: !!releaseId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('music_tracks')
        .select('*')
        .eq('release_id', releaseId!)
        .order('track_number', { ascending: true })

      if (error) throw error
      return data as MusicTrack[]
    },
  })
}

export function useCreateRelease() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (input: Omit<TablesInsert<'music_releases'>, 'tenant_id'>) => {
      const { data, error } = await supabase
        .from('music_releases')
        .insert({ ...input, tenant_id: tenantId! })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['music_releases', tenantId] })
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
      await triggerRevalidation('tenant-music')
    },
  })
}

export function useUpdateRelease() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({ id, ...update }: TablesUpdate<'music_releases'> & { id: string }) => {
      const { data, error } = await supabase
        .from('music_releases')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: async (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['music_releases', tenantId] })
      qc.invalidateQueries({ queryKey: ['music_releases', vars.id, 'detail'] })
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
      await triggerRevalidation('tenant-music')
    },
  })
}

export function useDeleteRelease() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('music_releases').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['music_releases', tenantId] })
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
      await triggerRevalidation('tenant-music')
    },
  })
}

// ============================================================
// TRACKS
// ============================================================

export function useCreateTrack() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (input: Omit<TablesInsert<'music_tracks'>, 'tenant_id'>) => {
      const { data, error } = await supabase
        .from('music_tracks')
        .insert({ ...input, tenant_id: tenantId! })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: async (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['music_tracks', vars.release_id] })
      await triggerRevalidation('tenant-music')
    },
  })
}

export function useUpdateTrack() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...update }: TablesUpdate<'music_tracks'> & { id: string }) => {
      const { data, error } = await supabase
        .from('music_tracks')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: async (data) => {
      qc.invalidateQueries({ queryKey: ['music_tracks', data.release_id] })
      await triggerRevalidation('tenant-music')
    },
  })
}

export function useDeleteTrack() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, releaseId }: { id: string; releaseId: string }) => {
      const { error } = await supabase.from('music_tracks').delete().eq('id', id)
      if (error) throw error
      return releaseId
    },
    onSuccess: async (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['music_tracks', vars.releaseId] })
      await triggerRevalidation('tenant-music')
    },
  })
}

export function useAllMusicTracks() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['music_tracks_all', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('music_tracks')
        .select('*, music_releases(title)')
        .eq('tenant_id', tenantId!)
        .order('track_number', { ascending: true })

      if (error) throw error
      return (data ?? []) as (MusicTrack & { music_releases: { title: string } | null })[]
    },
  })
}

// ============================================================
// COVER SONGS
// ============================================================

type CoverSong = Tables<'cover_songs'>

export function useCoverSongs() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['cover_songs', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cover_songs')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (error) throw error
      return data as CoverSong[]
    },
  })
}

export function useCreateCoverSong() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (input: Omit<TablesInsert<'cover_songs'>, 'tenant_id'>) => {
      const { data, error } = await supabase
        .from('cover_songs')
        .insert({ ...input, tenant_id: tenantId! })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cover_songs', tenantId] }),
  })
}

export function useUpdateCoverSong() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({ id, ...update }: TablesUpdate<'cover_songs'> & { id: string }) => {
      const { data, error } = await supabase
        .from('cover_songs')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cover_songs', tenantId] }),
  })
}

export function useDeleteCoverSong() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('cover_songs').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cover_songs', tenantId] }),
  })
}
